import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { Trophy, Users, Grid3x3, TrendingUp, Info, Edit2, Check, X } from 'lucide-react';
import { useTournament } from '@/context/TournamentContext';
import { toast } from 'sonner';

import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myPicks, setMyPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null); // {paid, payment_pending, payment_method}
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [funMessage, setFunMessage] = useState(null);
  const [showFunMessage, setShowFunMessage] = useState(false);
  const { currentTournament, selectedTournamentId } = useTournament();

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedTournamentId) {
        setLoading(false);
        return;
      }

      try {
        if (!user) {
          const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(userRes.data);
        }

        try {
          const leaderboardRes = await axios.get(
            `${API}/leaderboard?tournament_id=${selectedTournamentId}`
          );
          setLeaderboard(leaderboardRes.data); // Keep full leaderboard for calculations

          const picksRes = await axios.get(
            `${API}/picks/my?tournament_id=${selectedTournamentId}`,
            { withCredentials: true }
          );
          setMyPicks(picksRes.data);
          
          // Fetch payment status
          const participantRes = await axios.get(
            `${API}/participants/${selectedTournamentId}`,
            { withCredentials: true }
          );
          setPaymentStatus({
            paid: participantRes.data.paid,
            payment_pending: participantRes.data.payment_pending,
            payment_method: participantRes.data.payment_method
          });
        } catch (err) {
          // No active tournament
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, selectedTournamentId]);

  // Fetch fun message on every dashboard visit
  useEffect(() => {
    if (!selectedTournamentId || !user) return;
    
    const fetchFunMessage = async () => {
      try {
        const res = await axios.get(
          `${API}/fun-message?tournament_id=${selectedTournamentId}`,
          { withCredentials: true }
        );
        if (res.data?.message) {
          setFunMessage(res.data.message);
          setShowFunMessage(true);
        }
      } catch (err) {
        // Silently fail — not critical
      }
    };

    fetchFunMessage();
  }, [selectedTournamentId, user]);

  const handleMarkPaymentSent = async (paymentMethod) => {
    try {
      await axios.post(
        `${API}/tournaments/${selectedTournamentId}/mark-payment-sent`,
        { payment_method: paymentMethod },
        { withCredentials: true }
      );
      
      setPaymentStatus({
        paid: false,
        payment_pending: true,
        payment_method: paymentMethod
      });
      setShowPaymentModal(false);
      alert('Payment marked as sent! Admin will confirm.');
    } catch (error) {
      alert('Failed to mark payment. Please try again.');
    }
  };

  const handleEditName = () => {
    setNewName(user?.name || '');
    setEditingName(true);
  };

  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    try {
      await axios.put(
        `${API}/users/me/name`,
        { name: newName },
        { withCredentials: true }
      );
      
      // Update local user state
      setUser({ ...user, name: newName });
      setEditingName(false);
      toast.success('Your name has been updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update name');
    }
  };

  const handleCancelEditName = () => {
    setEditingName(false);
    setNewName('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent mx-auto mb-4"></div>
          <p className="font-bebas text-xl tracking-wider">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation user={user} />

      {/* Fun Message Modal */}
      {showFunMessage && funMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" onClick={() => setShowFunMessage(false)}>
          <div
            className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl border-2 border-amber-500 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-5xl mb-4">☘️</div>
            <p className="font-manrope text-lg text-gray-800 leading-relaxed mb-6">{funMessage}</p>
            <button
              data-testid="dismiss-fun-message"
              onClick={() => setShowFunMessage(false)}
              className="btn-primary px-8 py-2 rounded-md font-bebas text-lg tracking-wider"
            >
              Let's Go
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="bg-emerald-900 rounded-md p-8 mb-8 card-shadow text-white">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {editingName ? (
                <div className="flex items-center space-x-3 mb-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="px-3 py-2 border-2 border-white rounded-md font-bebas text-3xl text-emerald-900"
                    placeholder="Enter your name"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveName}
                    className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-md transition"
                    title="Save name"
                  >
                    <Check size={24} />
                  </button>
                  <button
                    onClick={handleCancelEditName}
                    className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-md transition"
                    title="Cancel"
                  >
                    <X size={24} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="font-bebas text-5xl tracking-wider">
                    Welcome, {user?.name}! ☘️
                  </h1>
                  <button
                    onClick={handleEditName}
                    className="bg-emerald-700 hover:bg-emerald-600 text-white p-2 rounded-md transition"
                    title="Edit your name"
                  >
                    <Edit2 size={20} />
                  </button>
                </div>
              )}
              <p className="font-manrope text-emerald-100 text-lg">
                {currentTournament
                  ? `${currentTournament.year} Tournament - ${currentTournament.status.toUpperCase()}`
                  : 'No active tournament yet'}
              </p>
            </div>
          </div>
        </div>

        {!currentTournament && (
          <div className="bg-amber-100 border-l-4 border-amber-600 p-6 mb-8 rounded-md">
            <p className="font-manrope text-amber-900">
              <strong>No active tournament.</strong> Check back soon or contact an admin.
            </p>
          </div>
        )}

        {currentTournament && (currentTournament.entry_fee || currentTournament.paypal_username || 
         currentTournament.venmo_username || currentTournament.cashapp_username) && (
          <div className="bg-white rounded-md card-shadow p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <span className="text-3xl">💰</span>
                <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
                  Entry Fee Payment
                </h2>
              </div>
              {paymentStatus && (
                <div>
                  {paymentStatus.paid ? (
                    <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-manrope font-bold text-sm">
                      ✓ Paid
                    </span>
                  ) : paymentStatus.payment_pending ? (
                    <span className="bg-amber-100 text-amber-800 px-4 py-2 rounded-full font-manrope font-bold text-sm">
                      ⏳ Pending Confirmation
                    </span>
                  ) : (
                    <span className="bg-red-100 text-red-800 px-4 py-2 rounded-full font-manrope font-bold text-sm">
                      ⚠️ Unpaid
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="bg-emerald-50 p-4 rounded-md">
              {currentTournament.entry_fee && (
                <p className="font-manrope text-lg text-emerald-900 mb-3">
                  <strong>Entry Fee:</strong> ${currentTournament.entry_fee}
                </p>
              )}
              <p className="font-manrope text-emerald-900 mb-3">
                <strong>Send payment to:</strong>
              </p>
              <div className="space-y-2 mb-4">
                {currentTournament.paypal_username && (
                  <div className="flex items-center space-x-2">
                    <span className="font-manrope font-bold text-emerald-900">PayPal:</span>
                    <a
                      href={`https://paypal.me/${currentTournament.paypal_username.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      @{currentTournament.paypal_username}
                    </a>
                  </div>
                )}
                {currentTournament.venmo_username && (
                  <div className="flex items-center space-x-2">
                    <span className="font-manrope font-bold text-emerald-900">Venmo:</span>
                    <a
                      href={`https://venmo.com/${currentTournament.venmo_username.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      @{currentTournament.venmo_username}
                    </a>
                  </div>
                )}
                {currentTournament.cashapp_username && (
                  <div className="flex items-center space-x-2">
                    <span className="font-manrope font-bold text-emerald-900">Cash App:</span>
                    <a
                      href={`https://cash.app/$${currentTournament.cashapp_username.replace('$', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:underline"
                    >
                      ${currentTournament.cashapp_username}
                    </a>
                  </div>
                )}
              </div>
              
              {paymentStatus && !paymentStatus.paid && !paymentStatus.payment_pending && (
                <div>
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-manrope font-bold py-3 px-6 rounded-md transition-colors"
                  >
                    I've Sent Payment
                  </button>
                </div>
              )}
              
              {paymentStatus && paymentStatus.payment_pending && (
                <div className="bg-amber-50 border border-amber-300 rounded-md p-3">
                  <p className="font-manrope text-amber-900 text-sm">
                    <strong>Payment pending confirmation.</strong> Admin will verify your payment soon.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="font-bebas text-2xl text-emerald-900 mb-4">Confirm Payment Method</h3>
              <p className="font-manrope text-gray-700 mb-4">
                How did you send the payment?
              </p>
              <div className="space-y-3">
                {currentTournament.venmo_username && (
                  <button
                    onClick={() => handleMarkPaymentSent('venmo')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-manrope font-bold py-3 px-4 rounded-md"
                  >
                    Venmo
                  </button>
                )}
                <button
                  onClick={() => handleMarkPaymentSent('offline')}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white font-manrope font-bold py-3 px-4 rounded-md"
                >
                  Cash / Other
                </button>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-manrope font-bold py-3 px-4 rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {currentTournament && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {/* Quick Stats */}
            <div
              data-testid="my-picks-card"
              onClick={() => navigate('/picks')}
              className="bg-white p-6 rounded-md card-shadow cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-emerald-900 w-12 h-12 rounded-full flex items-center justify-center">
                  <Grid3x3 className="text-amber-600" size={24} />
                </div>
                <span className="font-bebas text-4xl text-emerald-900">{myPicks.length}</span>
              </div>
              <h3 className="font-bebas text-xl text-emerald-900 tracking-wide">My Picks</h3>
              <p className="font-manrope text-gray-600 text-sm">Teams selected</p>
            </div>

            <div
              data-testid="leaderboard-card"
              onClick={() => navigate('/leaderboard')}
              className="bg-white p-6 rounded-md card-shadow cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-emerald-900 w-12 h-12 rounded-full flex items-center justify-center">
                  <Trophy className="text-amber-600" size={24} />
                </div>
                <span className="font-bebas text-4xl text-emerald-900">
                  {(() => {
                    const rank = leaderboard.findIndex(u => u.user_id === user?.user_id);
                    return rank >= 0 ? rank + 1 : '-';
                  })()}
                </span>
              </div>
              <h3 className="font-bebas text-xl text-emerald-900 tracking-wide">My Rank</h3>
              <p className="font-manrope text-gray-600 text-sm">Current position</p>
            </div>

            <div
              data-testid="max-points-card"
              onClick={() => navigate('/leaderboard')}
              className="bg-white p-6 rounded-md card-shadow cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="bg-emerald-900 w-12 h-12 rounded-full flex items-center justify-center">
                  <TrendingUp className="text-amber-600" size={24} />
                </div>
                <span className="font-bebas text-4xl text-emerald-900">
                  {(() => {
                    const userEntry = leaderboard.find(u => u.user_id === user?.user_id);
                    const maxScore = userEntry?.max_possible_points;
                    return maxScore != null ? maxScore : '—';
                  })()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <h3 className="font-bebas text-xl text-emerald-900 tracking-wide">Max Possible</h3>
                <div className="relative group">
                  <Info size={16} className="text-gray-400 cursor-help" />
                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                    <strong>Beta Feature</strong> - Max possible score may not be accurate. Available after Round 1 completes.
                  </div>
                </div>
              </div>
              <p className="font-manrope text-gray-600 text-sm">Best case scenario</p>
            </div>
          </div>
        )}

        {/* Top 5 Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="bg-white p-6 rounded-md card-shadow">
            <div className="flex items-center space-x-2 mb-4">
              <Trophy className="text-amber-600" size={28} />
              <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">Top 5</h2>
            </div>
            <div className="space-y-3">
              {leaderboard.slice(0, 5).map((entry, index) => (
                <div
                  key={entry.user_id}
                  data-testid={`leaderboard-entry-${index}`}
                  className={`flex items-center justify-between p-4 rounded-md ${
                    index === 0
                      ? 'rank-gold'
                      : index === 1
                      ? 'rank-silver'
                      : index === 2
                      ? 'rank-bronze'
                      : 'bg-gray-100'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <span className="font-bebas text-3xl text-emerald-900 w-8">{index + 1}</span>
                    {entry.picture && (
                      <img
                        src={entry.picture}
                        alt={entry.name}
                        className="w-10 h-10 rounded-full"
                      />
                    )}
                    <span className="font-manrope font-bold text-emerald-900">{entry.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bebas text-2xl text-emerald-900">{entry.total_points}</div>
                    <div className="font-manrope text-xs text-gray-600">points</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}